import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import alertService from "../../../_services/cart/alert.service";
import suscripcionService, { formatoFecha, formatoPrecio } from "../../../_services/cart/suscripcion.service";
import { ConfirmarAccion } from "../../vendedor/cuenta/ConfirmarAccion";
import tiendaService from "../../../_services/cart/tienda.service";

/**
 * La suscripcion de la cuenta del usuario, en su ficha del back office: plan, estado, uso, historial y
 * pagos, y las tres acciones a mano (cambiar el plan sin cobro, extender la prueba, cancelar).
 *
 * Para un empleado muestra la de la cuenta a la que pertenece: la suscripcion es de la cuenta.
 */

const ESTADO_LABEL = {
    ACTIVE: "Activa",
    PENDING: "Pendiente",
    PAUSED: "Pausada",
    CANCELLED: "Cancelada",
    EXPIRED: "Vencida",
};

const ORIGEN_LABEL = {
    APP: "La contrató el vendedor",
    TRIAL: "Prueba gratis",
    FUNDADOR: "Plan Fundador",
    MOCK: "Prueba local",
    ADMIN: "Asignada por el admin",
};

const Dato = ({ etiqueta, children }) =>
    children === undefined || children === null || children === "" ? null : (
        <div className="user-detail__dato">
            <p>{etiqueta}</p>
            <span>{children}</span>
        </div>
    );

export const SuscripcionUsuario = ({ userId }) => {
    const [detalle, setDetalle] = useState(null);
    const [planes, setPlanes] = useState([]);
    const [cargando, setCargando] = useState(true);

    const [modal, setModal] = useState(null); // "plan" | "prueba" | "cancelar"
    const [planId, setPlanId] = useState("");
    const [sinFin, setSinFin] = useState(true);
    const [dias, setDias] = useState("15");
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState("");

    const cargar = useCallback(() => {
        suscripcionService
            .adminDetalleDeUsuario(userId)
            .then((r) => {
                setDetalle(r.detalle);
                setPlanes(r.planes || []);
            })
            .catch((err) => alertService.error(err))
            .finally(() => setCargando(false));
    }, [userId]);

    useEffect(() => {
        if (userId) cargar();
    }, [userId, cargar]);

    const [subdominio, setSubdominio] = useState("");
    useEffect(() => setSubdominio(detalle?.subdominio || ""), [detalle?.subdominio]);

    if (cargando) return null;
    if (!detalle?.cuentaId) {
        return (
            <section className="user-detail__card">
                <h4>Suscripción</h4>
                <p className="user-detail__vacio">No vende: no tiene cuenta ni suscripción.</p>
            </section>
        );
    }

    const e = detalle.estado || {};

    const abrir = (cual) => {
        setError("");
        setPlanId(e.planId ? String(e.planId) : planes[0] ? String(planes[0].id) : "");
        setSinFin(true);
        setDias("15");
        setModal(cual);
    };

    const ejecutar = (promesa, okMsg) => {
        setGuardando(true);
        setError("");
        promesa
            .then(() => {
                alertService.msg(okMsg, "success");
                setModal(null);
                cargar();
            })
            .catch((err) => setError(err?.message || "No se pudo guardar"))
            .finally(() => setGuardando(false));
    };

    const guardarPlan = () => {
        if (!planId) return setError("Elegí un plan");
        if (!sinFin && !(Number(dias) > 0)) return setError("Indicá cuántos días");
        ejecutar(
            suscripcionService.adminAsignarPlan(detalle.cuentaId, Number(planId), sinFin ? null : Number(dias)),
            "Plan asignado"
        );
    };

    const guardarSubdominio = () => {
        tiendaService
            .adminFijarSubdominio(detalle.cuentaId, subdominio)
            .then(() => {
                alertService.msg(subdominio ? "Subdominio guardado" : "Subdominio liberado", "success");
                cargar();
            })
            .catch((err) => alertService.msg(err?.message || "No se pudo guardar", "error"));
    };

    const cambiarDesarmadero = (valor) => {
        suscripcionService
            .adminDesarmadero(detalle.cuentaId, valor)
            .then(() => {
                alertService.msg(valor ? "Ahora puede publicar bajas y motores" : "Ahora publica solo repuestos sueltos", "success");
                cargar();
            })
            .catch((err) => alertService.msg(err?.message || "No se pudo guardar", "error"));
    };

    const guardarPrueba = () => {
        if (!(Number(dias) > 0)) return setError("Indicá cuántos días");
        ejecutar(suscripcionService.adminExtenderPrueba(detalle.cuentaId, Number(dias)), "Prueba extendida");
    };

    const situacion = !e.tieneSuscripcion
        ? "Sin suscripción"
        : e.vigente
        ? e.trial
            ? `En prueba · ${e.diasRestantes} ${e.diasRestantes === 1 ? "día" : "días"}`
            : "Activa"
        : ESTADO_LABEL[e.status] || e.status;

    return (
        <section className="user-detail__card user-detail__card--ancha">
            <h4>Suscripción</h4>
            {!detalle.esDueno && (
                <p className="user-detail__vacio">Es empleado de la cuenta “{detalle.cuentaNombre}”: esta es la suscripción de esa cuenta.</p>
            )}

            <label className="suscripcion-admin__desarmadero">
                <input type="checkbox" checked={!!detalle.desarmadero} onChange={(ev) => cambiarDesarmadero(ev.target.checked)} />
                <span>
                    <strong>Rubro: usados (desarmadero)</strong>
                    <small>Publica usados, bajas y motores completos. Sin esto la cuenta es de repuestos nuevos: lo que publica sale como nuevo.</small>
                </span>
            </label>

            <div className="suscripcion-admin__subdominio">
                <span className="cuenta-usuarios__label">Tienda propia</span>
                <span className="tienda-config__subdominio">
                    <input type="text" value={subdominio} onChange={(ev) => setSubdominio(ev.target.value)} placeholder="sin subdominio" />
                    <span>.deautopartes.ar</span>
                </span>
                <button className="button" data-type="complementary-small" type="button"
                        disabled={(subdominio || "") === (detalle.subdominio || "")} onClick={guardarSubdominio}>
                    Guardar
                </button>
                <small>Se puede reservar aunque el plan no la incluya. Vacío lo libera.</small>
            </div>

            <div className="suscripcion-admin__resumen">
                <Dato etiqueta="Plan">{e.planNombre || "—"}</Dato>
                <Dato etiqueta="Situación">{situacion}</Dato>
                <Dato etiqueta="Monto mensual">{e.fundador ? "Sin cargo" : e.montoMensual != null ? formatoPrecio(e.montoMensual) : undefined}</Dato>
                {/* Solo de la vigente: de una cancelada o vencida, "vence" no dice nada util. */}
                <Dato etiqueta={e.trial ? "Prueba hasta" : "Vence"}>{!e.vigente ? undefined : e.endDate ? formatoFecha(e.endDate) : "Sin vencimiento"}</Dato>
                <Dato etiqueta="Próximo cobro">{e.nextPaymentDate ? formatoFecha(e.nextPaymentDate) : undefined}</Dato>
                <Dato etiqueta="Publicaciones activas">{`${e.publicacionesActivas ?? 0} / ${e.maxPublicacionesActivas ?? "ilimitado"}`}</Dato>
                <Dato etiqueta="Usuarios">{`${e.usuarios ?? 0} / ${e.maxUsers ?? "ilimitado"}`}</Dato>
                {e.pendientePago && <Dato etiqueta="Atención">Tiene un pago pendiente de autorizar en Mercado Pago</Dato>}
            </div>

            <div className="suscripcion-admin__acciones">
                <button className="button" data-type="secondary" type="button" onClick={() => abrir("plan")}>
                    <FontAwesomeIcon icon="fa-solid fa-award" /> Cambiar plan
                </button>
                <button className="button" data-type="secondary" type="button" onClick={() => abrir("prueba")}>
                    <FontAwesomeIcon icon="fa-solid fa-gift" /> Extender prueba
                </button>
                {e.vigente && (
                    <button className="button cuenta-usuarios__boton-peligro" type="button" onClick={() => setModal("cancelar")}>
                        <FontAwesomeIcon icon="fa-solid fa-ban" /> Cancelar
                    </button>
                )}
            </div>

            {detalle.historial?.length > 0 && (
                <details className="suscripcion-admin__historial">
                    <summary>Historial ({detalle.historial.length})</summary>
                    <table className="cuenta-usuarios__tabla">
                        <thead>
                            <tr>
                                <th>Alta</th>
                                <th>Plan</th>
                                <th>Estado</th>
                                <th>Origen</th>
                                <th>Vence</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detalle.historial.map((h) => (
                                <tr key={h.id}>
                                    <td>{formatoFecha(h.createdAt)}</td>
                                    <td>{h.planNombre}{h.trial ? " (prueba)" : ""}</td>
                                    <td>{ESTADO_LABEL[h.status] || h.status}</td>
                                    <td>{ORIGEN_LABEL[h.origen] || h.origen}</td>
                                    <td>{h.endDate ? formatoFecha(h.endDate) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}

            {detalle.pagos?.length > 0 && (
                <details className="suscripcion-admin__historial">
                    <summary>Pagos ({detalle.pagos.length})</summary>
                    <table className="cuenta-usuarios__tabla">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Concepto</th>
                                <th>Monto</th>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detalle.pagos.map((p) => (
                                <tr key={p.id}>
                                    <td>{formatoFecha(p.fechaPago)}</td>
                                    <td>{p.concepto}</td>
                                    <td>{formatoPrecio(p.amount)}</td>
                                    <td>{p.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}

            {modal === "plan" && (
                <div className="cuenta-usuarios__overlay" onClick={() => setModal(null)}>
                    <div className="cuenta-usuarios__modal cuenta-usuarios__modal--chico" onClick={(ev) => ev.stopPropagation()}>
                        <div className="cuenta-usuarios__modal-head">
                            <div>
                                <p className="cuenta-usuarios__eyebrow">SUSCRIPCIÓN</p>
                                <h3>Cambiar plan</h3>
                            </div>
                        </div>
                        <div className="cuenta-usuarios__modal-body">
                            <p className="cuenta-usuarios__texto">
                                Se le asigna sin cobro y reemplaza al plan actual. Si estaba pagando por Mercado Pago, esa
                                suscripción se cancela para que no se le siga cobrando.
                            </p>
                            <div>
                                <label className="cuenta-usuarios__label">Plan</label>
                                <select value={planId} onChange={(ev) => setPlanId(ev.target.value)}>
                                    {planes.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                            {Number(p.amount) > 0 ? ` (${formatoPrecio(p.amount)}/mes)` : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="suscripcion-admin__opciones">
                                <label>
                                    <input type="radio" checked={sinFin} onChange={() => setSinFin(true)} /> Sin vencimiento
                                </label>
                                <label>
                                    <input type="radio" checked={!sinFin} onChange={() => setSinFin(false)} /> Por
                                    <input type="text" className="suscripcion-admin__dias" value={dias} disabled={sinFin}
                                           onChange={(ev) => setDias(ev.target.value.replace(/\D/g, ""))} />
                                    días
                                </label>
                            </div>
                            {error && <div className="cuenta-usuarios__error">{error}</div>}
                        </div>
                        <div className="cuenta-usuarios__modal-foot">
                            <button type="button" className="button" data-type="secondary" onClick={() => setModal(null)}>Cancelar</button>
                            <button type="button" className="button" data-type="accent" disabled={guardando} onClick={guardarPlan}>
                                {guardando ? "Guardando..." : "Asignar plan"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {modal === "prueba" && (
                <div className="cuenta-usuarios__overlay" onClick={() => setModal(null)}>
                    <div className="cuenta-usuarios__modal cuenta-usuarios__modal--chico" onClick={(ev) => ev.stopPropagation()}>
                        <div className="cuenta-usuarios__modal-head">
                            <div>
                                <p className="cuenta-usuarios__eyebrow">SUSCRIPCIÓN</p>
                                <h3>Extender prueba</h3>
                            </div>
                        </div>
                        <div className="cuenta-usuarios__modal-body">
                            <p className="cuenta-usuarios__texto">
                                Si la prueba sigue vigente se le suman los días al vencimiento; si ya venció, se reactiva desde hoy.
                            </p>
                            <div>
                                <label className="cuenta-usuarios__label">Días</label>
                                <input type="text" value={dias} onChange={(ev) => setDias(ev.target.value.replace(/\D/g, ""))} />
                            </div>
                            {error && <div className="cuenta-usuarios__error">{error}</div>}
                        </div>
                        <div className="cuenta-usuarios__modal-foot">
                            <button type="button" className="button" data-type="secondary" onClick={() => setModal(null)}>Cancelar</button>
                            <button type="button" className="button" data-type="accent" disabled={guardando} onClick={guardarPrueba}>
                                {guardando ? "Guardando..." : "Extender"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {modal === "cancelar" && (
                <ConfirmarAccion
                    mensaje={`¿Cancelar la suscripción de “${detalle.cuentaNombre}”? No va a poder publicar ni sumar usuarios hasta tener un plan. Si paga por Mercado Pago, también se cancela ahí.`}
                    confirmLabel="Cancelar suscripción"
                    onConfirmar={() => ejecutar(suscripcionService.adminCancelar(detalle.cuentaId), "Suscripción cancelada")}
                    onCancelar={() => setModal(null)}
                />
            )}
        </section>
    );
};
